// useMainButton — drive the native Telegram MainButton from a React component
// (docs/miniapp-m2-tz.md §5.1). Used by the snooze / comment sheets so the
// confirm CTA is the native bottom button inside Telegram.
//
// Returns whether a MainButton is actually available: callers render their own
// in-sheet button as a fallback when it isn't (dev browser / old clients), so
// there's never a double CTA.
import { useEffect, useRef } from 'react'

import { getWebApp, isTelegramEnv } from './tg'

interface MainButtonOpts {
  text: string
  onClick: () => void
  enabled?: boolean
  visible?: boolean
}

export function useMainButton(opts: MainButtonOpts): boolean {
  // The SDK shim exposes a MainButton in a plain browser too, but it renders
  // nothing — so only treat it as available inside a real Telegram client.
  // Otherwise callers show their in-screen fallback button (dev / old clients).
  const available = !!getWebApp()?.MainButton && isTelegramEnv()
  // Keep the latest onClick in a ref so we register a single stable handler
  // (re-registering every render fights Telegram's click dedupe).
  const cb = useRef(opts.onClick)
  cb.current = opts.onClick

  // Register once; clean up (hide + unbind) on unmount.
  useEffect(() => {
    const mb = getWebApp()?.MainButton
    if (!mb) return
    const handler = () => cb.current()
    mb.onClick(handler)
    return () => {
      try { mb.offClick(handler) } catch { /* noop */ }
      try { mb.hide() } catch { /* noop */ }
    }
  }, [])

  // Reactively sync label / enabled / visible.
  useEffect(() => {
    const mb = getWebApp()?.MainButton
    if (!mb) return
    mb.setText(opts.text)
    if (opts.enabled === false) mb.disable()
    else mb.enable()
    if (opts.visible === false) mb.hide()
    else mb.show()
  }, [opts.text, opts.enabled, opts.visible])

  return available
}
